// Homepage global search - the one search box on the RentalVerse landing page that queries listed
// products across every vertical (GET /api/search) with free text + location + a filter panel
// (price, minimum rating, category, sort).
//
// Two presentations of the same state:
//   - Desktop (>= 768px): a dropdown of results below the hero search row; the filter panel is a
//     right-side Offcanvas opened from the "Filters" button.
//   - Mobile (< 768px): tapping the search box opens a full-screen overlay with its own sticky
//     search header pinned to the top (so the box is always at the top of the viewport), a
//     fixed close (X) button top-right, and the results in a list that scrolls the full height.
//     The same Offcanvas filter panel slides in over it.

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Badge, Button, Form, Offcanvas } from 'react-bootstrap';
import StarRating from './StarRating';
import { API_BASE_URL, listingImage, resultHref, rupees, verticalLabel, verticals } from './catalog';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 12;

const RATING_TIERS = [
  { value: '', label: 'Any' },
  { value: '3', label: '3.0+' },
  { value: '4', label: '4.0+' },
  { value: '4.5', label: '4.5+' },
];

const SORTS = [
  { value: 'relevance', label: 'Most relevant' },
  { value: 'rating', label: 'Top rated' },
  { value: 'priceLow', label: 'Price: low to high' },
  { value: 'priceHigh', label: 'Price: high to low' },
  { value: 'newest', label: 'Newest first' },
];

const PRICE_PRESETS = [
  { label: 'Under ₹5k', min: '', max: '5000' },
  { label: '₹5k – ₹25k', min: '5000', max: '25000' },
  { label: '₹25k – ₹1L', min: '25000', max: '100000' },
  { label: '₹1L +', min: '100000', max: '' },
];

const DEFAULT_FILTERS = { minPrice: '', maxPrice: '', minRating: '', vertical: '', sortBy: 'relevance' };

// jsdom has no matchMedia (setupTests.js stubs it to "not mobile"); real browsers get a live match.
function useIsMobile() {
  const query = '(max-width: 767.98px)';
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

function nonNegNumber(v) {
  const s = String(v).trim();
  return s !== '' && Number(s) >= 0 ? Number(s) : null;
}

function ResultRow({ item }) {
  return (
    <a className="rv-sx-result" href={resultHref(item)} aria-label={`View details for ${item.name} in ${verticalLabel(item.vertical)}`}>
      {item.image ? (
        <img className="rv-sx-thumb" src={listingImage(item.image)} alt="" loading="lazy" />
      ) : (
        <span className="rv-sx-thumb rv-sx-thumb--empty" aria-hidden="true">{item.name?.charAt(0) || '?'}</span>
      )}
      <span className="rv-sx-result-body">
        <span className="rv-sx-result-title">{item.name}</span>
        <span className="rv-sx-result-meta">
          <Badge bg="secondary" className="rv-sx-vbadge">{verticalLabel(item.vertical)}</Badge>
          {item.verified && <Badge bg="success" className="rv-sx-vbadge">✓ Verified</Badge>}
          {item.location ? <span className="rv-sx-result-loc">📍 {item.location}</span> : null}
        </span>
        <span className="rv-sx-result-foot">
          {item.ratingAvg ? <StarRating avg={item.ratingAvg} count={item.ratingCount} /> : <span className="rv-sx-new">New listing</span>}
          <strong className="rv-sx-price">{rupees(item.price, item.priceUnit)}</strong>
        </span>
      </span>
    </a>
  );
}

function ResultsBody({ searchState, results, pagination, onLoadMore }) {
  return (
    <div className="rv-sx-results-body">
      {searchState === 'loading' && <div className="rv-sx-note">Searching…</div>}
      {searchState === 'error' && (
        <div className="rv-sx-note rv-sx-note--error">
          Search is unavailable right now. Check the API is running, then try again.
        </div>
      )}
      {searchState === 'done' && results.length === 0 && (
        <div className="rv-sx-note">No listings match that search. Try fewer filters or a broader term.</div>
      )}

      {results.length > 0 && (
        <>
          {pagination?.total != null && (
            <div className="rv-sx-count">{pagination.total} result{pagination.total === 1 ? '' : 's'}</div>
          )}
          <div className="rv-sx-result-list">
            {results.map((item) => (
              <ResultRow key={`${item.vertical}-${item._id}`} item={item} />
            ))}
          </div>
          {pagination?.hasMore && (
            <div className="rv-sx-more">
              <Button
                variant="outline-primary"
                size="sm"
                disabled={searchState === 'loadingMore'}
                onClick={onLoadMore}
              >
                {searchState === 'loadingMore' ? 'Loading…' : 'Show more results'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function SearchExperience() {
  const isMobile = useIsMobile();

  const [term, setTerm] = useState('');
  const [location, setLocation] = useState('');
  const [localities, setLocalities] = useState([]);
  useEffect(() => { let alive = true; axios.get(`${API_BASE_URL}/api/search/localities`).then(response => { if (alive) setLocalities((response.data?.data || []).filter(value => typeof value === 'string')); }).catch(() => {}); return () => { alive = false; }; }, []);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS); // panel edits, applied on "Show results"

  const [showFilters, setShowFilters] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [searchState, setSearchState] = useState('idle'); // idle | loading | loadingMore | done | error
  const pageRef = useRef(1);
  const seqRef = useRef(0);
  const mobileInputRef = useRef(null);

  const { minPrice, maxPrice, minRating, vertical, sortBy } = filters;

  const hasQuery = Boolean(
    term.trim() || location.trim() || String(minPrice).trim() || String(maxPrice).trim() || minRating || vertical
  );

  const activeFilterCount =
    (String(minPrice).trim() || String(maxPrice).trim() ? 1 : 0) +
    (minRating ? 1 : 0) +
    (vertical ? 1 : 0) +
    (sortBy && sortBy !== 'relevance' ? 1 : 0);

  const runSearch = useCallback(
    async (page, append) => {
      const params = { limit: PAGE_SIZE, page };
      if (term.trim()) params.q = term.trim();
      if (location.trim()) params.location = location.trim();
      if (nonNegNumber(minPrice) != null) params.minPrice = nonNegNumber(minPrice);
      if (nonNegNumber(maxPrice) != null) params.maxPrice = nonNegNumber(maxPrice);
      if (minRating) params.minRating = Number(minRating);
      if (vertical) params.vertical = vertical;
      if (sortBy && sortBy !== 'relevance') params.sortBy = sortBy;

      const seq = ++seqRef.current;
      setSearchState(append ? 'loadingMore' : 'loading');
      try {
        const res = await axios.get(`${API_BASE_URL}/api/search`, { params });
        if (seq !== seqRef.current) return; // a newer search superseded this one
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        setResults((prev) => (append ? [...prev, ...rows] : rows));
        setPagination(res.data?.pagination || null);
        setSearchState('done');
      } catch {
        if (seq !== seqRef.current) return;
        if (!append) {
          setResults([]);
          setPagination(null);
        }
        setSearchState('error');
      }
    },
    [term, location, minPrice, maxPrice, minRating, vertical, sortBy]
  );

  // Debounced fetch whenever the query or any applied filter changes - always resets to page 1.
  useEffect(() => {
    if (!hasQuery) {
      seqRef.current++; // cancel any in-flight result
      setResults([]);
      setPagination(null);
      setSearchState('idle');
      return undefined;
    }
    const t = setTimeout(() => {
      pageRef.current = 1;
      runSearch(1, false);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [hasQuery, runSearch]);

  const loadMore = () => {
    pageRef.current += 1;
    runSearch(pageRef.current, true);
  };

  // Mobile: opening the overlay pins the search box to the top of the viewport and locks the page
  // behind it; focus the overlay's own input so typing continues seamlessly.
  const openMobile = useCallback(() => {
    if (!isMobile) return;
    setMobileOpen(true);
  }, [isMobile]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const id = window.setTimeout(() => mobileInputRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prev;
      window.clearTimeout(id);
    };
  }, [mobileOpen]);

  // Leaving mobile width (rotate / resize) tears the overlay down so we don't strand it open.
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const closeMobile = () => {
    setMobileOpen(false);
    setTerm('');
    setLocation('');
  };

  const openFilters = () => {
    setDraftFilters(filters);
    setShowFilters(true);
  };
  const applyFilters = () => {
    setFilters(draftFilters);
    setShowFilters(false);
    // On mobile the filter panel can be opened straight from the hero (overlay still closed) -
    // make sure the results it just filtered are actually on screen.
    if (isMobile) setMobileOpen(true);
  };
  const clearFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
  };
  const setDraft = (patch) => setDraftFilters((f) => ({ ...f, ...patch }));

  const filtersButton = (
    <Button
      type="button"
      variant="primary"
      className="rv-sx-filter-fab"
      onClick={openFilters}
      aria-label="Open filters"
    >
      <span aria-hidden="true">⚙</span> Filters
      {activeFilterCount > 0 && <Badge bg="light" text="dark" className="ms-1">{activeFilterCount}</Badge>}
    </Button>
  );

  const searchFields = (variant) => (
    <>
      <Form.Control
        ref={variant === 'mobile' ? mobileInputRef : undefined}
        className="rv-sx-input"
        aria-label="Search all listings"
        placeholder="Search homes, cars, stays, jewellery…"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={variant === 'hero' ? openMobile : undefined}
      />
      <Form.Control
        className="rv-sx-loc"
        aria-label="Location"
        placeholder="Location"
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        onFocus={variant === 'hero' ? openMobile : undefined}
      />
      <Form.Select aria-label="Locality dropdown" className="rv-sx-loc" value={location} onChange={e => setLocation(e.target.value)}>
        <option value="">All localities</option>
        {[...new Set([location, ...localities].filter(Boolean))].map(value => <option key={value} value={value}>{value}</option>)}
      </Form.Select>
    </>
  );

  return (
    <>
      {/* ---- Hero search row (always rendered) ---- */}
      <Form
        className="rv-sx"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          if (isMobile) openMobile();
          if (hasQuery) {
            pageRef.current = 1;
            runSearch(1, false);
          }
        }}
      >
        <div className="rv-sx-row">
          {searchFields('hero')}
          <Button type="submit" variant="light" className="rv-sx-go" disabled={!hasQuery && !isMobile}>
            Search
          </Button>
        </div>
      </Form>

      {/* ---- Desktop: inline results dropdown ---- */}
      {!isMobile && hasQuery && (
        <div className="rv-sx-results rv-sx-results--desktop">
          <ResultsBody
            searchState={searchState}
            results={results}
            pagination={pagination}
            onLoadMore={loadMore}
          />
        </div>
      )}

      {/* ---- Mobile: full-screen overlay with the search box pinned to the top ---- */}
      {isMobile && mobileOpen && (
        <div className="rv-sx-overlay" role="dialog" aria-modal="true" aria-label="Search listings">
          <div className="rv-sx-overlay-head">
            <Form
              className="rv-sx rv-sx--overlay"
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                if (hasQuery) {
                  pageRef.current = 1;
                  runSearch(1, false);
                }
              }}
            >
              <div className="rv-sx-row">
                {searchFields('mobile')}
              </div>
            </Form>
            <div className="rv-sx-overlay-actions">
              <button
                type="button"
                className="rv-sx-close"
                aria-label="Close search results"
                onClick={closeMobile}
              >
                ×
              </button>
            </div>
          </div>
          <div className="rv-sx-overlay-scroll">
            {!hasQuery ? (
              <div className="rv-sx-note">Start typing to search across every vertical.</div>
            ) : (
              <ResultsBody
                searchState={searchState}
                results={results}
                pagination={pagination}
                onLoadMore={loadMore}
              />
            )}
          </div>
        </div>
      )}

      {filtersButton}

      {/* ---- Filter panel (right-side, shared by desktop + mobile) ---- */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="end"
        className="rv-sx-panel"
        backdropClassName="rv-sx-panel-backdrop"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Filters</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <section className="rv-sx-fgroup">
            <h6>Sort by</h6>
            <Form.Select
              aria-label="Sort by"
              value={draftFilters.sortBy}
              onChange={(e) => setDraft({ sortBy: e.target.value })}
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Form.Select>
          </section>

          <section className="rv-sx-fgroup">
            <h6>Price range</h6>
            <div className="rv-sx-chips">
              {PRICE_PRESETS.map((p) => {
                const active = draftFilters.minPrice === p.min && draftFilters.maxPrice === p.max;
                return (
                  <button
                    key={p.label}
                    type="button"
                    className={`rv-sx-chip${active ? ' rv-sx-chip--on' : ''}`}
                    onClick={() => setDraft({ minPrice: active ? '' : p.min, maxPrice: active ? '' : p.max })}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="rv-sx-minmax">
              <Form.Control
                type="number"
                min="0"
                inputMode="numeric"
                aria-label="Minimum price"
                placeholder="Min ₹"
                value={draftFilters.minPrice}
                onChange={(e) => setDraft({ minPrice: e.target.value })}
              />
              <span>–</span>
              <Form.Control
                type="number"
                min="0"
                inputMode="numeric"
                aria-label="Maximum price"
                placeholder="Max ₹"
                value={draftFilters.maxPrice}
                onChange={(e) => setDraft({ maxPrice: e.target.value })}
              />
            </div>
          </section>

          <section className="rv-sx-fgroup">
            <h6>Minimum rating</h6>
            <div className="rv-sx-chips">
              {RATING_TIERS.map((t) => (
                <button
                  key={t.value || 'any'}
                  type="button"
                  className={`rv-sx-chip${draftFilters.minRating === t.value ? ' rv-sx-chip--on' : ''}`}
                  onClick={() => setDraft({ minRating: t.value })}
                >
                  {t.value ? `★ ${t.label}` : t.label}
                </button>
              ))}
            </div>
          </section>

          <section className="rv-sx-fgroup">
            <h6>Category</h6>
            <div className="rv-sx-radios">
              <label className="rv-sx-radio">
                <input
                  type="radio"
                  name="rv-sx-vertical"
                  checked={!draftFilters.vertical}
                  onChange={() => setDraft({ vertical: '' })}
                />
                <span>All categories</span>
              </label>
              {verticals.map((v) => (
                <label key={v.verticalKey} className="rv-sx-radio">
                  <input
                    type="radio"
                    name="rv-sx-vertical"
                    checked={draftFilters.vertical === v.verticalKey}
                    onChange={() => setDraft({ vertical: v.verticalKey })}
                  />
                  <span>{v.icon} {v.name}</span>
                </label>
              ))}
            </div>
          </section>
        </Offcanvas.Body>
        <div className="rv-sx-panel-foot">
          <Button variant="link" className="rv-sx-clear" onClick={clearFilters}>Clear all</Button>
          <Button variant="primary" onClick={applyFilters}>
            {hasQuery && pagination?.total != null ? `Show ${pagination.total} result${pagination.total === 1 ? '' : 's'}` : 'Show results'}
          </Button>
        </div>
      </Offcanvas>
    </>
  );
}
